/**
 * InviteAccept — Feb 2026.
 *
 * Public page (no auth). Reached via /invite/:token. Consumes a single-use
 * invite generated by an admin, lets the invitee set their own password, and
 * logs them into the platform on success.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Headset, ArrowRight, ShieldCheck, Warning, CircleNotch } from "@phosphor-icons/react";
import SdmLogo from "@/components/SdmLogo";
import { AuthHeroDesktop, AuthHeroMobile } from "@/components/AuthHero";

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const { lang } = useLang();
  const T = (es, en) => (lang === "es" ? es : en);

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/auth/invite/${token}`)
      .then((r) => { if (alive) { setInfo(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || T("Invitación inválida", "Invalid invite")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  if (done) return <Navigate to="/" replace />;

  const canSubmit = pw.length >= 8 && pw === pw2 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/auth/invite/${token}/accept`, { password: pw });
      localStorage.setItem("sdm_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("sdm_refresh", data.refresh_token);
      localStorage.setItem("sdm_user", JSON.stringify(data.user));
      setUser(data.user);
      toast.success(info?.purpose === "reset" ? T("Contraseña actualizada.", "Password updated.") : T("¡Bienvenido! Contraseña creada.", "Welcome! Password set."));
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || T("No se pudo aceptar la invitación", "Could not accept invite"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      <div className="relative flex flex-col justify-between p-8 lg:p-14">
        <AuthHeroMobile />
        <SdmLogo size={56} />

        <div className="w-full max-w-lg mx-auto lg:mx-0 py-16">
          <div className="flex items-center gap-3 mb-10 animate-fade-in">
            <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center shrink-0">
              <Headset size={24} weight="duotone" className="text-accent-foreground" />
            </div>
            <div className="font-heading font-extrabold text-lg leading-tight tracking-[-0.03em]">SDM Customer Engagement Console</div>
          </div>

          {loading && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <CircleNotch size={16} className="animate-spin" />
              {T("Validando invitación…", "Validating invite…")}
            </div>
          )}

          {!loading && error && (
            <div data-testid="invite-error" className="border border-destructive/40 bg-destructive/10 rounded-lg p-4 text-sm">
              <div className="flex items-center gap-2 text-destructive font-semibold mb-1">
                <Warning size={16} />
                {T("Enlace no válido", "Invalid link")}
              </div>
              <div className="text-muted-foreground">{error}</div>
              <div className="mt-3 text-xs text-muted-foreground">
                {T("Pide un enlace nuevo en \"¿Olvidaste tu contraseña?\" o pídeselo a tu supervisor.", "Request a new link via \"Forgot your password?\" or ask your supervisor.")}
              </div>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => nav("/login")}>
                {T("Ir al login", "Go to login")}
              </Button>
            </div>
          )}

          {!loading && info && (
            <>
              <h1 className="font-heading text-5xl font-extrabold tracking-[-0.035em] leading-[1.02] mb-3 animate-fade-in" data-testid="invite-title">
                {info.purpose === "reset" ? T("Nueva contraseña", "New password") : T("Crea tu contraseña", "Set your password")}
              </h1>
              <p className="text-base text-muted-foreground mb-10 max-w-md">
                {info.purpose === "reset"
                  ? T("Crea una nueva contraseña para", "Create a new password for")
                  : T("Bienvenido a bordo. Termina de configurar tu acceso en", "Welcome aboard. Finish setting up access for")}{" "}
                <span className="font-semibold text-foreground">{info.email}</span>.
              </p>

              <form onSubmit={submit} className="space-y-5" data-testid="invite-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                    {T("Nueva contraseña (mín 8)", "New password (min 8)")}
                  </Label>
                  <Input
                    type="password"
                    autoFocus
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="mt-2 h-14 rounded-xl bg-input border-0 text-base px-5 focus-visible:ring-2 focus-visible:ring-primary"
                    data-testid="invite-pw"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                    {T("Confirmar contraseña", "Confirm password")}
                  </Label>
                  <Input
                    type="password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="mt-2 h-14 rounded-xl bg-input border-0 text-base px-5 focus-visible:ring-2 focus-visible:ring-primary"
                    data-testid="invite-pw2"
                    placeholder="••••••••"
                  />
                  {pw2 && pw !== pw2 && (
                    <div className="text-xs text-destructive mt-2">{T("Las contraseñas no coinciden", "Passwords do not match")}</div>
                  )}
                </div>

                <Button type="submit" disabled={!canSubmit} className="w-full h-14 rounded-xl text-base font-semibold" data-testid="invite-submit">
                  {submitting ? <CircleNotch size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} className="mr-2" />}
                  {T("Crear contraseña e ingresar", "Create password & sign in")}
                  <ArrowRight size={14} className="ml-2" />
                </Button>
              </form>

              <div className="mt-6 text-xs text-muted-foreground">
                {T("Este enlace expira el ", "This link expires on ")}
                <span className="font-semibold">{new Date(info.expires_at).toLocaleString()}</span>
                {T(" y solo puede usarse una vez.", " and can only be used once.")}
              </div>
            </>
          )}
        </div>

        <div className="text-xs text-muted-foreground">
          © 2026 · Santo Domingo Motors · República Dominicana
        </div>
      </div>

      <AuthHeroDesktop />
    </div>
  );
}
